<template>
    <section
        v-if="formConfig && content && countries"
        id="c-referral-form"
        :key="forceRerender"
        class="c-referral-form"
    >
        <div
            class="c-referral-form__title"
        >
            <h2>
                {{ content.title }}
            </h2>
        </div>
        <div class="c-referral-form__bottom-section">
            <div class="c-referral-form__form">
                <!-- name and email -->
                <div class="c-referral-form__form--dual">
                    <div
                        v-if="formConfig.commenting_name !== 'no_receive'"
                        class="input-container"
                    >
                        <label
                            class="form-label"
                            :class="{'forced-data': formConfig.commenting_name === 'required'}"
                            for="name"
                        >
                            {{ $t('referral-program.full_name') }}
                        </label>
                        <y-input
                            id="name"
                            v-model="model.name"
                            name="your_name"
                            no-label
                            :placeholder="$t('referral-program.full_name')"
                            class="mt-2"
                        />
                    </div>

                    <div
                        v-if="formConfig.commenting_email !== 'no_receive'"
                        class="input-container"
                    >
                        <label
                            class="form-label"
                            :class="{'forced-data': formConfig.commenting_email === 'required'}"
                            for="email"
                        >
                            {{ $t('referral-program.email_address') }}
                        </label>
                        <y-input
                            id="email"
                            v-model="model.email"
                            no-label
                            name="your_email"
                            :placeholder="$t('referral-program.email_address')"
                            class="mt-2"
                        />
                    </div>
                </div>
                <!-- mobile and country -->
                <div class="c-referral-form__form--dual">
                    <div
                        v-if="formConfig.commenting_mobile !== 'no_receive'"
                        class="input-container"
                    >
                        <label
                            class="form-label"
                            :class="{'forced-data': formConfig.commenting_mobile === 'required'}"
                            for="mobile"
                        >
                            {{ $t('referral-program.phone_number') }}
                        </label>
                        <y-phone
                            v-model="model.mobile"
                            class="mt-2"
                            name="mobile"
                            :placeholder="$t('fields.mobile')"
                            :translations="{
                                countrySelectorLabel: $t('fields.country_selector_label'),
                                countrySelectorError: $t('fields.country_selector_error'),
                                phoneNumberLabel: $t('fields.phone_number_label'),
                                example: $t('fields.example')
                            }"
                            no-example
                            no-label
                            @update="onUpdateMobile"
                        />
                    </div>

                    <!-- Country selector -->
                    <div
                        v-if="formConfig.commenting_country !== 'no_receive'"
                        class="input-container"
                    >
                        <label
                            class="form-label"
                            :class="{'forced-data': formConfig.commenting_country === 'required'}"
                            for="country"
                        >
                            {{ $t('referral-program.country') }}
                        </label>

                        <y-select
                            id="country"
                            :options="countries"
                            no-label
                            :placeholder="$t('referral-program.country')"
                            class="m-schedule__fields-wrapper__fields__select mt-2"
                            @select="selectedCountry = $event"
                        />
                    </div>
                </div>
                <!-- message -->
                <div
                    v-if="formConfig.commenting_text !== 'no_receive'"
                    class="input-container"
                >
                    <label
                        class="form-label"
                        :class="{'forced-data': formConfig.commenting_text === 'required'}"
                        for="textarea"
                    >
                        {{ $t('referral-program.message') }}
                    </label>
                    <y-textarea
                        id="textarea"
                        v-model="model.text"
                        no-label
                        name="message"
                        class="mt-2"
                        rows="5"
                    />
                </div>

                <y-button
                    flat
                    :title="$t('referral-program.message')"
                    color="primary"
                    loading-on="saving-referral-form"
                    @click.prevent="beforeSubmit"
                >
                    {{ $t('referral-program.submit') }}
                </y-button>
            </div>
            <div
                class="c-referral-form__image"
            >
                <img
                    :src="imgSrc(content.image)"
                    :alt="content.title"
                >
            </div>
        </div>

        <y-recaptcha
            v-if="formConfig && formConfig.recaptcha"
            ref="recaptcha"
            :site-key="formConfig.recaptcha.key"
            :size="formConfig.recaptcha?.size"
            @verify="afterVerify"
        />
    </section>
</template>

<script >
    import { isLink } from '@/helpers/helpers';

    import {
        YInput, YRecaptcha, YTextarea, YSelect, YPhone,
    } from '@groot-components/all';

    export default {
        name: 'ReferralProgramForm',

        components: {
            YPhone,
            YInput,
            YRecaptcha,
            YTextarea,
            YSelect,
        },

        /**
         *
         * @inheritDoc
         */
        props: {
            content   : Object,
            formConfig: Object,
            countries : Array,
        },

        /**
         * @inheritDoc
         */
        data() {
            return {
                forceRerender  : 0,
                phoneObject    : {},
                selectedCountry: {},
                model          : {
                    name   : '',
                    email  : '',
                    mobile : '',
                    text   : '',
                    country: '',
                },
            };
        },

        /**
         * @inheritDoc
         */
        mounted() {
            this.scrollHandler();
        },

        /**
         *
         * @inheritDoc
         */
        methods: {
            isLink,

            /**
             * scroll handler
             */
            scrollHandler() {
                const targetElement = document.getElementById('c-referral-form');
                document.querySelectorAll('.scroll-to-referral-form').forEach((item) => {
                    item.addEventListener('click', () => {
                        targetElement.scrollIntoView({
                            behavior: 'smooth',
                        });
                    });
                });

                // Removes the label on phone number
                document.querySelector('.country-selector__label').remove();
            },

            /**
             * Verify recaptcha
             * Check recaptcha is active
             */
            beforeSubmit() {
                this.$wait.start('saving-referral-form');
                if (this.formConfig?.recaptcha?.active === '1') {
                    this.$refs.recaptcha.verify();
                } else {
                    this.save();
                }
            },
            /**
             * Update phone component
             *
             * @param {object} value
             */
            onUpdateMobile(value) {
                this.phoneObject = value;
            },

            /**
             * Set token and start Sending Comment
             *
             * @param {string} token
             */
            afterVerify(token) {
                this.model['g-recaptcha-response'] = token;
                this.save();
            },

            /**
             * Check the Promise.all Request Error
             *
             * @param {Array} req - Responses array
             * @param {index} index - number of response
             * @returns {boolean}
             */
            handleReq(req, index) {
                return req && req[index] && !req[index].isAxiosError;
            },

            /**
             * Do send comment
             *
             * @returns {object}
             */
            save() {
                return this.$services.Commenting.commentSave({
                    ...this.model,
                    country: this.selectedCountry.value,
                    mobile : {
                        country_code: `+${this.phoneObject.countryCallingCode || 1}`,
                        mobile      : this.phoneObject.nationalNumber || '',
                    },
                    type  : 'pages',
                    slug  : 'referral-program',
                    locale: this.$i18n.locale,
                }).then(() => {
                    this.model = {
                        name   : '',
                        email  : '',
                        mobile : '',
                        text   : '',
                        country: '',
                    };
                    this.selectedCountry = this.countries.length ? this.countries.find((item) => item.label.includes('USA')) : {};
                    this.$toast.success(this.$t('referral-program.submit_success_msg'));
                    this.forceRerender += 1;
                }).catch((error) => {
                    this.handleError(error, this.$t('general.footer.error_msg'));
                }).finally(() => {
                    this.$wait.end('saving-referral-form');
                    this.$refs.recaptcha.reset();
                    this.scrollHandler();
                });
            },
        },
    };

</script>

<style lang="scss" src="./index.scss"/>
